import React, { useState } from 'react'

import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Modal,
  Stack,
  Typography,
} from '@mui/material'

import { useMutationAcceptLegalDocuments } from '@/api/users/mutations'

import { LEGAL_DOCUMENTS, LegalDocumentKey } from '@/utils/legal'

import { LegalAcceptanceModalProps } from './types'

const LegalAcceptanceModal: React.FC<LegalAcceptanceModalProps> = ({ documents, onAccepted }) => {
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [error, setError] = useState<string | null>(null)

  const { mutateAsync, isPending } = useMutationAcceptLegalDocuments()

  const allChecked = documents.length > 0 && documents.every(document => checked[document])

  const handleToggle = (document: LegalDocumentKey) => {
    setChecked(previous => ({ ...previous, [document]: !previous[document] }))
  }

  const handleConfirm = async () => {
    setError(null)

    const payload = documents.reduce(
      (acc, document) => ({ ...acc, [LEGAL_DOCUMENTS[document].userField]: true }),
      {}
    )

    try {
      await mutateAsync(payload)
      setChecked({})
      onAccepted()
    } catch {
      setError('Não foi possível registrar o aceite. Por favor, tente novamente.')
    }
  }

  return (
    <Modal open aria-describedby="legal-acceptance-modal-description">
      <Stack
        direction="column"
        width={350}
        bgcolor="background.paper"
        borderRadius={4}
        padding={2}
        spacing={2}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Typography variant="h6" textAlign="center">
          Atualizamos nossos documentos
        </Typography>

        <Typography id="legal-acceptance-modal-description" variant="body2" color="text.secondary">
          Para continuar usando o aplicativo, é necessário aceitar a versão vigente dos documentos abaixo.
        </Typography>

        <Stack direction="column">
          {documents.map(document => (
            <FormControlLabel
              key={document}
              control={<Checkbox checked={!!checked[document]} onChange={() => handleToggle(document)} />}
              label={
                <Typography variant="body2">
                  Li e aceito os{' '}
                  <Link
                    href={LEGAL_DOCUMENTS[document].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {LEGAL_DOCUMENTS[document].label}
                  </Link>
                </Typography>
              }
            />
          ))}
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!allChecked}
          loading={isPending}
          sx={{ borderRadius: 20 }}
          disableElevation
        >
          Aceitar e continuar
        </Button>
      </Stack>
    </Modal>
  )
}

export default LegalAcceptanceModal
